/**
 * M2.3 covers → YouTube thumbnails. One card for a version's cover options, used on the review screen and the post detail.
 * Two modes, so the review screen never shows two competing cover controls:
 *  - `selection` given (a version awaiting review): the tiles are a radiogroup whose pick goes with Approve (saved then);
 *  - otherwise: each tile has "Choose", which saves at once (PUT /api/versions/:id/cover) and the worker sets the thumbnail.
 * Both can upload a new cover (POST /api/uploads → POST /api/versions/:id/covers).
 */
import { useId, useRef, useState } from "react";
import { COVER_CONTENT_TYPES, COVER_MAX_BYTES } from "@contentops/contract";
import type { CoverView, VersionCovers, VersionDetail } from "../../api/types";
import { Icon } from "../../components/Icon";
import { useToast } from "../../components/toast";
import { Badge, Button, Card, ErrorNote, Loading, Progress } from "../../components/ui";
import { useApi, useCache, useMutation } from "../../data/hooks";
import { useVersionCovers, versionCoversKey } from "../../data/queries";

type Props = {
  versionId: string;
  /** Review mode: the pick is held by the screen and sent with Approve. */
  selection?: { value: string | undefined; onChange: (artifactId: string) => void };
  /** Shown while the covers load (e.g. the version's own `cover` artifacts), so the card doesn't flash empty. */
  fallback?: VersionCovers;
  /** Extra cache keys a choose makes stale (the post detail's own publication, for its thumbnail state). */
  invalidate?: string[];
  hint?: string;
};

/** The covers the version detail already carries, in the covers shape (used until GET …/covers answers). */
export function coversFromVersion(d: VersionDetail): VersionCovers {
  return {
    version_id: d.version.id,
    chosen_artifact_id: d.version.chosen_cover_artifact_id ?? null,
    covers: d.artifacts.filter((a) => a.kind === "cover").map((a) => ({
      artifact_id: a.id, filename: a.filename, content_type: a.content_type, bytes: a.bytes ?? 0, sha256: "", created_at: d.version.created_at, source: "studio",
      label: typeof a.meta.label === "string" ? a.meta.label : null, url: "", width: null, height: null, settable: true, problem: null,
    })),
  };
}

/** The cover Approve should send: the owner's pick, else the chosen one, else the first YouTube can take. */
export function defaultCover(c: VersionCovers | undefined, picked: string | undefined): string | undefined {
  return picked ?? c?.chosen_artifact_id ?? c?.covers.find((x) => x.settable)?.artifact_id;
}

const coverName = (c: CoverView, i: number) => c.label || `Cover ${i + 1}`;
const ACCEPT = COVER_CONTENT_TYPES.join(",");

export function CoverPicker({ versionId, selection, fallback, invalidate = [], hint }: Props) {
  const api = useApi();
  const cache = useCache();
  const toast = useToast();
  const q = useVersionCovers(versionId);
  const data = q.data ?? fallback;
  const fileRef = useRef<HTMLInputElement>(null);
  const [upload, setUpload] = useState<number | null>(null);
  const [choosing, setChoosing] = useState<string | null>(null);
  const hid = useId();
  const store = (r: VersionCovers) => cache.setData(versionCoversKey(versionId), r);

  const choose = useMutation((artifactId: string) => api.chooseCover(versionId, artifactId), {
    // version/<id> (its chosen cover) + whatever shows the thumbnail state; the covers key is written directly.
    invalidate: [`version/${versionId}`, ...invalidate],
    onSuccess: store,
  });
  const onChoose = async (c: CoverView, i: number) => {
    setChoosing(c.artifact_id);
    try {
      await choose.mutateAsync(c.artifact_id);
      toast.ok(`“${coverName(c, i)}” is the cover now — it becomes the YouTube thumbnail.`);
    } catch (e) {
      toast.error(e, "Couldn't choose that cover");
    } finally {
      setChoosing(null);
    }
  };

  const onFile = async (file: File | undefined) => {
    if (fileRef.current) fileRef.current.value = ""; // picking the same file again still fires change
    if (!file) return;
    // Checked here too so a phone doesn't spend minutes uploading something the API (and YouTube) would refuse.
    if (!(COVER_CONTENT_TYPES as readonly string[]).includes(file.type)) { toast.show({ tone: "danger", text: "A cover must be a PNG or JPEG image." }); return; }
    if (file.size > COVER_MAX_BYTES) { toast.show({ tone: "danger", text: `That image is ${Math.ceil(file.size / 1024 / 1024)} MB — YouTube takes thumbnails up to ${COVER_MAX_BYTES / 1024 / 1024} MB.` }); return; }
    setUpload(0);
    try {
      const { artifact_id } = await api.upload(file, { kind: "other", onProgress: setUpload });
      const r = await api.attachCover(versionId, artifact_id);
      store(r);
      cache.invalidate(`version/${versionId}`);
      // The same image already attached comes back as the existing cover (a different id than this upload).
      if (selection) selection.onChange((r.covers.find((c) => c.artifact_id === artifact_id) ?? r.covers.at(-1))!.artifact_id);
      toast.ok(selection ? "Cover added and selected — it's saved when you approve." : "Cover added. Tap Choose to use it.");
    } catch (e) {
      toast.error(e, "Couldn't add the cover");
    } finally {
      setUpload(null);
    }
  };

  const uploadButton = (
    <>
      <Button size="sm" variant="ghost" icon="upload" busy={upload !== null} onClick={() => fileRef.current?.click()}>Upload cover</Button>
      <input ref={fileRef} type="file" accept={ACCEPT} hidden aria-label="Cover image file" onChange={(e) => void onFile(e.target.files?.[0])} />
    </>
  );

  return (
    <Card title="Cover" action={uploadButton}>
      {hint && <p className="muted small">{hint}</p>}
      {upload !== null && <Progress value={upload} label="Uploading cover" />}
      {!data ? (q.error ? <ErrorNote error={q.error} onRetry={() => q.refetch()} /> : <Loading label="Loading covers…" />)
        : !data.covers.length ? <p className="muted small">No cover yet. Upload a PNG or JPEG — YouTube uses it as the thumbnail.</p>
        : selection ? (
          <div className="covers" role="radiogroup" aria-label="Choose a cover">
            {data.covers.map((c, i) => (
              <div key={c.artifact_id} className="cover-item">
                <button type="button" role="radio" aria-checked={selection.value === c.artifact_id} aria-label={coverName(c, i)} className="cover"
                  onClick={() => selection.onChange(c.artifact_id)} disabled={!c.settable} aria-describedby={c.problem ? `${hid}-${i}` : undefined}>
                  <img src={api.artifactUrl(c.artifact_id)} alt="" loading="lazy" />
                  {selection.value === c.artifact_id && <span className="cover-check"><Icon name="check" size={18} /></span>}
                </button>
                {c.problem && <p id={`${hid}-${i}`} className="cover-problem">{c.problem}</p>}
              </div>
            ))}
          </div>
        ) : (
          <ul className="covers" aria-label="Covers">
            {data.covers.map((c, i) => {
              const chosen = data.chosen_artifact_id === c.artifact_id;
              return (
                <li key={c.artifact_id} className="cover-item" aria-label={coverName(c, i)}>
                  <div className={`cover${chosen ? " is-chosen" : ""}`}>
                    <img src={api.artifactUrl(c.artifact_id)} alt={coverName(c, i)} loading="lazy" />
                    {chosen && <span className="cover-check"><Icon name="check" size={18} /></span>}
                  </div>
                  {chosen ? <Badge tone="ok">Chosen</Badge> : (
                    <Button size="sm" variant="secondary" disabled={!c.settable || (choose.pending && choosing !== c.artifact_id)} busy={choosing === c.artifact_id}
                      aria-describedby={c.problem ? `${hid}-${i}` : undefined} onClick={() => void onChoose(c, i)}>Choose</Button>
                  )}
                  {c.problem && <p id={`${hid}-${i}`} className="cover-problem">{c.problem}</p>}
                </li>
              );
            })}
          </ul>
        )}
    </Card>
  );
}
